import {
  AiAssistantApprovalCard,
  AiAssistantApprovalCardRow,
  AiAssistantApprovalResponse,
  AiAssistantToolPart,
} from "@/components/ai-assistant/AiAssistantApprovalCard.tsx";
import useGoalsHook from "@/hooks/use-goals.hook.tsx";
import { useGoal } from "@/queries/goals.query.ts";
import { ApiCustomFieldValue } from "@/types/custom-field.types.ts";
import {
  ApiGoalConfidenceLevel,
  ApiGoalStatus,
  GoalConfidenceLevelItems,
  GoalStatusItems,
} from "@/types/goals.types.ts";
import { SelectItem } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

interface CheckinGoalInput {
  goal_id?: number;
  key_results?: { key_result_id: number; value?: ApiCustomFieldValue }[];
  status?: ApiGoalStatus;
  confidence_level?: ApiGoalConfidenceLevel;
  comment?: string | null;
}

const itemTitle = (items: SelectItem[], id: string | undefined) =>
  items.find((item) => item.id === id)?.title ?? id;

const change = (before: string | undefined, after: string | undefined) =>
  before !== undefined && before !== after ? `${before} → ${after}` : after;

interface AiAssistantCheckInCardProps {
  part: AiAssistantToolPart;
  onApprovalResponse: AiAssistantApprovalResponse;
}

export function AiAssistantCheckInCard({ part, onApprovalResponse }: AiAssistantCheckInCardProps) {
  const { t } = useTranslation();
  const { renderKeyResultValue } = useGoalsHook();
  const input = (part.input ?? {}) as CheckinGoalInput;
  const { data: goal } = useGoal(input.goal_id ?? 0, !!input.goal_id);

  const rows: AiAssistantApprovalCardRow[] = (input.key_results ?? []).map(
    ({ key_result_id, value }) => {
      const keyResult = goal?.goal_key_results.find((item) => item.id === key_result_id);
      if (!keyResult) {
        return {
          label: `#${key_result_id}`,
          value: String(value?.value_float ?? value?.value_bool ?? ""),
        };
      }
      return {
        label: keyResult.title,
        value: change(
          renderKeyResultValue(keyResult.current_value, keyResult.metric_type),
          renderKeyResultValue({ ...keyResult.current_value, ...value }, keyResult.metric_type)
        ),
      };
    }
  );

  if (input.status) {
    rows.push({
      label: t("common:status"),
      value: change(
        goal && itemTitle(GoalStatusItems(), goal.status),
        itemTitle(GoalStatusItems(), input.status)
      ),
    });
  }
  if (input.confidence_level) {
    rows.push({
      label: t("goals:confidence_level"),
      value: change(
        goal && itemTitle(GoalConfidenceLevelItems(), goal.confidence_level),
        itemTitle(GoalConfidenceLevelItems(), input.confidence_level)
      ),
    });
  }
  if (input.comment) {
    rows.push({ label: t("common:comment"), value: input.comment });
  }

  return (
    <AiAssistantApprovalCard
      part={part}
      title={t("ai:assistant_action_checkin_goal")}
      subtitle={goal?.title}
      rows={rows}
      onApprovalResponse={onApprovalResponse}
    />
  );
}
